import { computed, makeObservable } from 'mobx';
import { InputGroupChangeTracker } from '../../change_trackers';
import { SectionPair } from '../../types';
import type SectionModel from '../nodes/SectionModel';
import type { ComparisonInputGroup } from './ComparisonNodeBaseModel';

export default class SectionComparisonGroupModel implements ComparisonInputGroup {
    private readonly tracker: InputGroupChangeTracker | undefined;

    constructor(
        readonly id: string,
        readonly panelId: string,
        readonly inputIds: string[],
        readonly pair: SectionPair,
        private readonly newSection: SectionModel | undefined,
        private readonly previousSection: SectionModel | undefined
    ) {
        // A matched pair retains its baseline even when this node has fields on only one side.
        if (pair.kind === 'matched') {
            const tracker = new InputGroupChangeTracker();

            inputIds.forEach(inputId => {
                tracker.createInputChangeTracker(
                    inputId,
                    this.getNewInputById(inputId),
                    this.getPreviousInputById(inputId)
                );
            });

            this.tracker = tracker;
        }

        makeObservable(this, {
            isHidden: computed,
            isDisabled: computed,
            changes: computed
        });
    }

    dispose() {
        this.tracker?.clearInputChangeTrackers();
    }

    getNewInputById(inputId: string) {
        return this.newSection?.getInputById(inputId);
    }

    getPreviousInputById(inputId: string) {
        return this.previousSection?.isHidden ? undefined : this.previousSection?.getInputById(inputId);
    }

    get isHidden() {
        return (this.newSection ?? this.previousSection)?.isHidden;
    }

    get isDisabled() {
        return this.newSection?.isDisabled;
    }

    get changes() {
        if (this.tracker) {
            return this.tracker.aggregatedChanges;
        }

        // Unmatched sections have no previous field baseline. Keep edits to new fields visible.
        return InputGroupChangeTracker.aggregateChanges(
            this.inputIds.flatMap(inputId => {
                const newInput = this.getNewInputById(inputId);

                return newInput?.hasUnsavedChanges
                    ? [{ inputId, inputName: newInput.name, newInput, changeType: 'unsaved' as const }]
                    : [];
            })
        );
    }
}
